import { useState, useRef } from "react";
import { ChevronLeft, ChevronRight, Maximize2, X } from "lucide-react";

const TOTAL_PAGES = 24;
const PAGES = Array.from({ length: TOTAL_PAGES }, (_, i) =>
  `/menu/page-${String(i + 1).padStart(2, "0")}.png`
);

export const PriceList = () => {
  const [index, setIndex] = useState(0);
  const [zoom, setZoom] = useState(null);
  const trackRef = useRef(null);

  // Single page swipe — one big page at a time on all viewports
  const perView = 1;

  const maxIndex = Math.max(0, TOTAL_PAGES - perView);
  const safeIndex = Math.min(index, maxIndex);

  const next = () => setIndex((i) => Math.min(i + 1, maxIndex));
  const prev = () => setIndex((i) => Math.max(i - 1, 0));

  return (
    <section
      id="price-list"
      data-testid="price-list-section"
      className="py-16 lg:py-24 bg-[#F1EDDE]"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-10">
        <div className="text-center mb-10 reveal">
          <div className="text-[0.6rem] uppercase tracking-[0.32em] mb-3" style={{ color: "#B89968" }}>
            Stacy Fox · Laser &amp; Aesthetics
          </div>
          <h2
            className="font-serif font-light leading-[0.95]"
            style={{
              color: "#161618",
              fontSize: "clamp(2.5rem, 6vw, 4.75rem)",
              letterSpacing: "0.02em",
            }}
          >
            The Brochure
          </h2>
          <div
            className="mt-5 font-serif italic"
            style={{ color: "#B89968", fontSize: "clamp(1.25rem, 2.4vw, 1.85rem)" }}
          >
            Every treatment, every price.
          </div>
          <div className="flex items-center justify-center gap-3 mt-6">
            <span className="h-px w-12" style={{ background: "#B89968" }} />
            <span className="text-xs" style={{ color: "#B89968" }}>♥</span>
            <span className="h-px w-12" style={{ background: "#B89968" }} />
          </div>
          <p
            className="mt-7 text-[0.72rem] sm:text-sm uppercase tracking-[0.28em] leading-relaxed"
            style={{ color: "#161618" }}
          >
            Flip through · Zoom in · Or download the full PDF
          </p>
        </div>

        <div className="flex items-center justify-end gap-4 mb-8 reveal">
          <a
            href="/menu/menu.pdf"
            download
            data-testid="brochure-download"
            className="hidden md:inline-flex text-[0.7rem] uppercase tracking-[0.25em] text-[#161618] hover:text-[#B89968] link-underline"
          >
            Download PDF
          </a>
          <div className="flex items-center gap-2">
            <button
              onClick={prev}
              disabled={safeIndex === 0}
              data-testid="brochure-prev"
              aria-label="Previous page"
              className="w-12 h-12 border border-[#161618] flex items-center justify-center text-[#161618] hover:bg-[#161618] hover:text-[#F1EDDE] transition-all disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={next}
              disabled={safeIndex >= maxIndex}
              data-testid="brochure-next"
              aria-label="Next page"
              className="w-12 h-12 border border-[#161618] flex items-center justify-center text-[#161618] hover:bg-[#161618] hover:text-[#F1EDDE] transition-all disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Carousel */}
        <div
          className="relative overflow-hidden border border-[#C9BEA9] bg-[#FFFFFF] shadow-[0_30px_80px_-30px_rgba(62,57,50,0.35)]"
          data-testid="brochure-viewport"
        >
          {/* Overlay prev/next */}
          <button
            onClick={prev}
            disabled={safeIndex === 0}
            data-testid="brochure-overlay-prev"
            aria-label="Previous page"
            className="absolute top-1/2 -translate-y-1/2 left-3 sm:left-5 z-20 w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-[#161618]/85 backdrop-blur-sm text-[#F1EDDE] flex items-center justify-center hover:bg-[#161618] transition-all disabled:opacity-30 disabled:cursor-not-allowed shadow-lg"
            style={{ border: "1px solid #B89968" }}
          >
            <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
          <button
            onClick={next}
            disabled={safeIndex >= maxIndex}
            data-testid="brochure-overlay-next"
            aria-label="Next page"
            className="absolute top-1/2 -translate-y-1/2 right-3 sm:right-5 z-20 w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-[#161618]/85 backdrop-blur-sm text-[#F1EDDE] flex items-center justify-center hover:bg-[#161618] transition-all disabled:opacity-30 disabled:cursor-not-allowed shadow-lg"
            style={{ border: "1px solid #B89968" }}
          >
            <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
          <div
            ref={trackRef}
            className="flex transition-transform duration-700"
            style={{
              transform: `translateX(-${(safeIndex * 100) / perView}%)`,
            }}
          >
            {PAGES.map((src, i) => (
              <div
                key={src}
                className="shrink-0 flex items-center justify-center p-6 sm:p-10"
                style={{ width: `${100 / perView}%` }}
              >
                <button
                  onClick={() => setZoom(i)}
                  data-testid={`brochure-page-${i + 1}`}
                  className="group relative block w-full"
                  aria-label={`Open page ${i + 1} in full screen`}
                >
                  <img
                    src={src}
                    alt={`Treatment brochure page ${i + 1}`}
                    loading={i < 4 ? "eager" : "lazy"}
                    className="block w-full h-auto shadow-md border border-[#E6DFCC]"
                  />
                  <span className="absolute top-3 right-3 w-9 h-9 bg-[#161618]/0 group-hover:bg-[#161618]/85 text-[#F1EDDE] opacity-0 group-hover:opacity-100 flex items-center justify-center transition-all">
                    <Maximize2 className="w-4 h-4" />
                  </span>
                  <span className="absolute bottom-3 left-3 text-[0.6rem] uppercase tracking-[0.25em] text-[#8C8580] bg-[#FFFFFF]/80 px-2 py-1">
                    Page {i + 1} / {TOTAL_PAGES}
                  </span>
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Progress bar */}
        <div className="mt-8 flex items-center gap-6">
          <div className="flex-1 h-px bg-[#C9BEA9] relative overflow-hidden">
            <div
              className="absolute inset-y-0 left-0 bg-[#161618] transition-all duration-700"
              style={{
                width: `${((safeIndex + perView) / TOTAL_PAGES) * 100}%`,
              }}
            />
          </div>
          <span
            data-testid="brochure-progress"
            className="text-[0.65rem] uppercase tracking-[0.25em] text-[#4D4A47] whitespace-nowrap font-mono"
          >
            {String(safeIndex + 1).padStart(2, "0")} / {TOTAL_PAGES}
          </span>
        </div>
      </div>

      {/* Zoom modal */}
      {zoom !== null && (
        <div
          data-testid="brochure-zoom-modal"
          className="fixed inset-0 z-[90] bg-[#161618]/95 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8"
          onClick={() => setZoom(null)}
        >
          <button
            data-testid="brochure-zoom-close"
            onClick={() => setZoom(null)}
            className="absolute top-6 right-6 w-12 h-12 border border-[#F1EDDE] text-[#F1EDDE] flex items-center justify-center hover:bg-[#F1EDDE] hover:text-[#161618] transition-all"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
          <img
            src={PAGES[zoom]}
            alt={`Treatment brochure page ${zoom + 1}`}
            className="max-w-full max-h-[88vh] shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-[0.65rem] uppercase tracking-[0.3em] text-[#F1EDDE]">
            Page {zoom + 1} of {TOTAL_PAGES} · click outside to close
          </div>
        </div>
      )}
    </section>
  );
};

export default PriceList;
